/**
 * Shared low-level objective/action state reduction over the event log.
 *
 * Split out from `projection.ts` so that both the view projections
 * (`projection.ts`) and the date-realism forecast (`../forecast/
 * objective-forecast.ts`) can depend on this without depending on each
 * other -- `projection.ts` embeds a forecast in `ObjectiveView`, and the
 * forecast needs the same action/objective state, so a shared base avoids a
 * projection <-> forecast import cycle.
 *
 * Modeling note (ADR-0002 addendum 2026-07-02): actions are flat, ordered,
 * one-off checkable items scoped to one objective; "milestone" is an
 * optional free-text tag carried on the action, not a separate entity.
 *
 * Phase 5 additions (ADR-0002 addendum 2026-07-04): the `draft`/`active`/
 * `on_hold`/`archived` status state machine (`resolveEffectiveState`), the
 * `to_do`/`doing`/`done` action status (`resolveActionDisplayStatus`), the
 * organizational fields (category/effort/priority/reminder/cadence goal/
 * visibility toggles, all carried on `objective.edited.changes`), per-action
 * effort minutes, and the symmetric objective-to-objective link resolver
 * (`resolveObjectiveLinks`).
 */

import type {
  ActionAddedEvent,
  ActionId,
  ActionStatus,
  ActionsReorderedEvent,
  CompassEvent,
  EventId,
  ObjectiveCategory,
  ObjectiveId,
  ObjectiveLevelOfEffort,
  ObjectivePriority,
  ObjectiveReminder,
  ObjectiveStatus,
} from '../../contracts';
import {
  eventsOfTypes,
  excludingReverted,
  revertedEventIds,
} from '../event-log/event-log';

export interface ObjectiveState {
  readonly objectiveId: ObjectiveId;
  title: string;
  why: string;
  targetDate: string;
  /**
   * The eventId of the winning (active, non-reverted) `objective
   * .target_date_adjusted` event that produced `targetDate`, or null when
   * `targetDate` is still the original from `objective.created` (no
   * adjustment applied, or every adjustment was reverted). Feeds
   * `ObjectiveForecastView.appliedAdjustmentEventId` (domain-api.ts),
   * mirroring `ActionView.entryEventId` so the UI can one-tap undo an
   * accepted date adjustment via the shared `revertLogEntry`.
   */
  appliedAdjustmentEventId: EventId | null;
  /** occurredOn of the objective.created event -- the forecast's window start. */
  readonly createdOn: string;
  /**
   * @deprecated derived convenience, `=== (status === 'archived')`. Kept so
   * every pre-Phase-5 call site that reads `.archived` keeps compiling and
   * behaving identically; new code should read `.status` instead (Phase 5,
   * ADR-0002 addendum 2026-07-04 D2).
   */
  archived: boolean;
  /**
   * The eventId of the winning (surviving, non-reverted) `objective.archived`
   * event when `archived` is true, or null when active. Feeds
   * `ObjectiveSummaryView.archivedEventId` (domain-api.ts), mirroring
   * `appliedAdjustmentEventId` above so the UI can one-tap un-archive via the
   * shared `revertLogEntry`. Phase 5: also populated from the newer
   * `objective.status_changed` path (see `resolveEffectiveState`).
   */
  archivedEventId: EventId | null;
  /**
   * The persisted lifecycle state (R3.9, Phase 5): `resolveEffectiveState`'s
   * output. Source of truth for Today-surfacing/forecast/cap/weekly-review
   * gating (ADR-0002 addendum 2026-07-04 D1/D2).
   */
  status: ObjectiveStatus;
  /** The event responsible for `status`, or null when it falls out of the implicit default (see `resolveEffectiveState`). */
  statusEventId: EventId | null;
  /** R3.8. Distinct enum from `HabitCategory`; null = uncategorized. */
  category: ObjectiveCategory | null;
  /** R3.15. Organizational only. */
  levelOfEffort: ObjectiveLevelOfEffort | null;
  priority: ObjectivePriority | null;
  reminder: ObjectiveReminder | null;
  /** R3.12. User-stated actions/week goal; null = unset. */
  cadenceGoalPerWeek: number | null;
  /** R3.14 visibility toggles -- all default true. */
  includeInWeeklyReview: boolean;
  surfaceOnToday: boolean;
  ambientRewardOnComplete: boolean;
  /**
   * Pin state (R8.8, Phase 6, ADR-0002 addendum 2026-07-07) -- resolved from
   * the latest surviving `objective.pin_changed` event, falling back to
   * `objective.created.initialPinned ?? false` (mirrors `status`'s
   * `initialStatus` fallback chain, D-PinnedObjectives). Explicitly no
   * effect on progress/forecast/XP/the 5-active cap (AC-T19).
   */
  pinned: boolean;
  pinnedOrder: number | null;
  /** Actions keyed by actionId; order is tracked separately in `order`. */
  readonly actionsById: Map<ActionId, ActionDraftState>;
  /** Display order of actionIds, per `action.added`/`actions.reordered`. */
  order: ActionId[];
}

export interface ActionDraftState {
  readonly actionId: ActionId;
  title: string;
  dueDate: string | null;
  milestoneTag: string | null;
  /** R3.15, Phase 5. Per-action effort estimate in whole minutes; null = unset. */
  effortMinutes: number | null;
  removed: boolean;
}

const OBJECTIVE_EVENT_TYPES = [
  'objective.created',
  'objective.edited',
  'objective.target_date_adjusted',
  'objective.archived',
  'objective.status_changed',
  'objective.pin_changed',
  'action.added',
  'action.edited',
  'action.removed',
  'action.completed',
  'action.status_changed',
  'actions.reordered',
  'log_entry.reverted',
] as const;

/**
 * Build the full per-objective state map from the raw log.
 *
 * `log_entry.reverted` is consulted for four things:
 * - Completion status, computed separately by `computeActionCompletionStatus`
 *   below (ADR-0002: `action.completed` is a valid revert target).
 * - The effective `targetDate`, resolved by `resolveEffectiveTargetDate`
 *   below: `objective.target_date_adjusted` is also a valid revert target
 *   (mirrors `action.completed`'s "as if absent" semantics) -- a reverted
 *   adjustment must not apply, and the objective falls back to the prior
 *   surviving adjustment, or `objective.created`'s original `targetDate` if
 *   none remain. Unlike action completion status (a per-action presence
 *   set), target date is a single mutable field with a most-recent-wins
 *   resolution, so this needs its own walk rather than reusing
 *   `computeActionCompletionStatus`'s pattern directly.
 * - The effective lifecycle `status`, resolved by `resolveEffectiveState`
 *   below (Phase 5, ADR-0002 addendum 2026-07-04 D1/D2): both the legacy
 *   `objective.archived` event and the newer `objective.status_changed`
 *   event are valid revert targets.
 * - Per-action display `status` (`to_do`/`doing`), resolved by
 *   `resolveActionDisplayStatus` below (Phase 5, D3): `action.status_changed`
 *   is a valid revert target, though completion (`action.completed`) always
 *   wins regardless of any status_changed history.
 *
 * Other lifecycle events (created/edited/added/removed/reordered) are never
 * valid revert targets (ADR-0002).
 */
export function buildObjectiveStates(
  events: readonly CompassEvent[],
): ReadonlyMap<ObjectiveId, ObjectiveState> {
  const relevant = eventsOfTypes(events, OBJECTIVE_EVENT_TYPES);
  const states = new Map<ObjectiveId, ObjectiveState>();

  for (const event of relevant) {
    switch (event.type) {
      case 'objective.created': {
        const state: ObjectiveState = {
          objectiveId: event.objectiveId,
          title: event.title,
          why: event.why,
          targetDate: event.targetDate,
          appliedAdjustmentEventId: null,
          createdOn: event.occurredOn,
          archived: false,
          archivedEventId: null,
          status: 'draft',
          statusEventId: null,
          category: null,
          levelOfEffort: null,
          priority: null,
          reminder: null,
          cadenceGoalPerWeek: null,
          includeInWeeklyReview: true,
          surfaceOnToday: true,
          ambientRewardOnComplete: true,
          pinned: false,
          pinnedOrder: null,
          actionsById: new Map(),
          order: [],
        };
        for (const action of event.actions) {
          state.actionsById.set(action.actionId, {
            actionId: action.actionId,
            title: action.title,
            dueDate: action.dueDate,
            milestoneTag: action.milestoneTag,
            effortMinutes: action.effortMinutes ?? null,
            removed: false,
          });
          state.order.push(action.actionId);
        }
        states.set(event.objectiveId, state);
        break;
      }
      case 'objective.edited': {
        const state = states.get(event.objectiveId);
        if (!state) break;
        if (event.changes.title !== undefined) state.title = event.changes.title;
        if (event.changes.why !== undefined) state.why = event.changes.why;
        if (event.changes.category !== undefined) state.category = event.changes.category;
        if (event.changes.levelOfEffort !== undefined) {
          state.levelOfEffort = event.changes.levelOfEffort;
        }
        if (event.changes.priority !== undefined) state.priority = event.changes.priority;
        if (event.changes.reminder !== undefined) state.reminder = event.changes.reminder;
        if (event.changes.cadenceGoalPerWeek !== undefined) {
          state.cadenceGoalPerWeek = event.changes.cadenceGoalPerWeek;
        }
        if (event.changes.includeInWeeklyReview !== undefined) {
          state.includeInWeeklyReview = event.changes.includeInWeeklyReview;
        }
        if (event.changes.surfaceOnToday !== undefined) {
          state.surfaceOnToday = event.changes.surfaceOnToday;
        }
        if (event.changes.ambientRewardOnComplete !== undefined) {
          state.ambientRewardOnComplete = event.changes.ambientRewardOnComplete;
        }
        break;
      }
      case 'objective.target_date_adjusted': {
        // Applied unconditionally here (raw log order); a reverted
        // adjustment is corrected afterwards by `resolveEffectiveTargetDate`
        // below, once the full set of reverted eventIds is known. Applying
        // it provisionally in-order keeps this switch a single forward pass
        // over the log, matching every other case here.
        const state = states.get(event.objectiveId);
        if (!state) break;
        state.targetDate = event.nextTargetDate;
        break;
      }
      // objective.archived, objective.status_changed, and
      // objective.pin_changed are all applied in the second pass below
      // (resolveEffectiveState / resolvePinState), mirroring
      // objective.target_date_adjusted: a reverted archive/status/pin change
      // must fall back correctly (ADR-0011 Decision 3 /
      // phase-2d-management-history.md MC-3 un-archive; ADR-0002 addendum
      // 2026-07-04 D1/D2 for the status-machine path; ADR-0002 addendum
      // 2026-07-07 D-PinnedObjectives for pin/order).
      case 'action.added': {
        const state = states.get(event.objectiveId);
        if (!state) break;
        applyActionAdded(state, event);
        break;
      }
      case 'action.edited': {
        const state = states.get(event.objectiveId);
        if (!state) break;
        const action = state.actionsById.get(event.actionId);
        if (!action) break;
        if (event.changes.title !== undefined) action.title = event.changes.title;
        if (event.changes.dueDate !== undefined) action.dueDate = event.changes.dueDate;
        if (event.changes.milestoneTag !== undefined) {
          action.milestoneTag = event.changes.milestoneTag;
        }
        if (event.changes.effortMinutes !== undefined) {
          action.effortMinutes = event.changes.effortMinutes;
        }
        break;
      }
      case 'action.removed': {
        const state = states.get(event.objectiveId);
        if (!state) break;
        const action = state.actionsById.get(event.actionId);
        if (action) action.removed = true;
        state.order = state.order.filter((id) => id !== event.actionId);
        break;
      }
      case 'actions.reordered': {
        const state = states.get(event.objectiveId);
        if (!state) break;
        applyActionsReordered(state, event);
        break;
      }
      // action.completed, action.status_changed, and log_entry.reverted do
      // not affect the objective/action metadata state directly;
      // action.completed affects completion status
      // (computeActionCompletionStatus below), action.status_changed affects
      // display status (resolveActionDisplayStatus below), and
      // log_entry.reverted is consulted by the resolvers below.
      default:
        break;
    }
  }

  for (const state of states.values()) {
    const resolvedTargetDate = resolveEffectiveTargetDate(relevant, state.objectiveId);
    state.targetDate = resolvedTargetDate.targetDate;
    state.appliedAdjustmentEventId = resolvedTargetDate.appliedAdjustmentEventId;

    const resolvedState = resolveEffectiveState(relevant, state.objectiveId);
    state.status = resolvedState.status;
    state.statusEventId = resolvedState.statusEventId;
    state.archived = resolvedState.status === 'archived';
    state.archivedEventId = resolvedState.status === 'archived' ? resolvedState.statusEventId : null;

    const resolvedPin = resolvePinState(relevant, state.objectiveId);
    state.pinned = resolvedPin.pinned;
    state.pinnedOrder = resolvedPin.pinnedOrder;
  }

  return states;
}

interface ResolvedObjectiveState {
  readonly status: ObjectiveStatus;
  /**
   * The winning `objective.status_changed` event's ID, the legacy
   * `objective.archived` event's ID, or null when `status` falls out of the
   * implicit default (no status_changed event, no archived event -- see the
   * `initialStatus` fallback below).
   */
  readonly statusEventId: EventId | null;
}

/**
 * The effective lifecycle state for one objective (R3.9, Phase 5, ADR-0002
 * addendum 2026-07-04 D1/D2) -- mirrors `resolveEffectiveTargetDate`'s exact
 * shape: single mutable field ("status"), most-recent-surviving-write
 * resolution, explicit fallback chain.
 *
 * Resolution order:
 * 1. A winning (last, non-reverted) `objective.status_changed` event for
 *    this objective, if one exists -- the newer mechanism always supersedes
 *    once anything has ever been written through it (there is no code path
 *    that produces both a legacy archive AND a later status_changed for the
 *    same objective today, but if one existed, status_changed correctly
 *    wins here as "the newer mechanism").
 * 2. Else, a surviving legacy `objective.archived` event, if one exists --
 *    100% of today's pre-Phase-5 behavior, byte-for-byte, for every
 *    objective that predates this phase (`resolveEffectiveArchived`'s exact
 *    former logic, preserved verbatim as this branch).
 * 3. Else, `objective.created.initialStatus ?? 'active'` -- the backfill
 *    rule: a NEWLY authored objective (Phase 5 onward) always stamps
 *    `initialStatus: 'draft'` explicitly, so it correctly starts as a draft;
 *    a pre-Phase-5 objective has no `initialStatus` field at all (the field
 *    did not exist yet), which resolves to `'active'` so existing objectives
 *    keep surfacing on Today/forecast/review exactly as they did before this
 *    phase shipped -- never silently reinterpreted as a draft.
 */
function resolveEffectiveState(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
): ResolvedObjectiveState {
  const reverted = revertedEventIds(events);

  let winningStatusChanged: { readonly status: ObjectiveStatus; readonly eventId: EventId } | null =
    null;
  let legacyArchivedEventId: EventId | null = null;
  let initialStatus: 'draft' | 'active' | undefined;

  for (const event of events) {
    if (event.type === 'objective.created' && event.objectiveId === objectiveId) {
      initialStatus = event.initialStatus;
    } else if (
      event.type === 'objective.status_changed' &&
      event.objectiveId === objectiveId &&
      !reverted.has(event.eventId)
    ) {
      // Canonical (eventId) order means later matches in this forward walk
      // are more recent -- last surviving status_changed wins.
      winningStatusChanged = { status: event.nextStatus, eventId: event.eventId };
    } else if (
      event.type === 'objective.archived' &&
      event.objectiveId === objectiveId &&
      !reverted.has(event.eventId)
    ) {
      legacyArchivedEventId = event.eventId;
    }
  }

  if (winningStatusChanged) {
    return { status: winningStatusChanged.status, statusEventId: winningStatusChanged.eventId };
  }
  if (legacyArchivedEventId !== null) {
    return { status: 'archived', statusEventId: legacyArchivedEventId };
  }
  return { status: initialStatus ?? 'active', statusEventId: null };
}

interface ResolvedPinState {
  readonly pinned: boolean;
  readonly pinnedOrder: number | null;
}

/**
 * Pin/order resolution (R8.8, Phase 6, ADR-0002 addendum 2026-07-07
 * D-PinnedObjectives) -- mirrors `resolveEffectiveTargetDate`'s exact shape:
 * the last surviving (non-reverted) `objective.pin_changed` event wins;
 * absence resolves to `objective.created.initialPinned ?? false` (with
 * `pinnedOrder: null`, since an unpinned objective has no order to carry --
 * `initialPinned` has no accompanying order field on `objective.created` by
 * design, matching how no build-time UI this phase sets it there).
 * `objective.pin_changed` is NOT in `VALID_REVERT_TARGET_TYPES` (tap-again-
 * to-unpin is the natural undo), but `revertedEventIds` is still consulted
 * here defensively for consistency with every other resolver in this file --
 * a future allowlist addition would then "just work" with no change here.
 */
function resolvePinState(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
): ResolvedPinState {
  const reverted = revertedEventIds(events);

  let winning: { readonly pinned: boolean; readonly pinnedOrder: number | null } | null = null;
  let initialPinned: boolean | undefined;

  for (const event of events) {
    if (event.type === 'objective.created' && event.objectiveId === objectiveId) {
      initialPinned = event.initialPinned;
    } else if (
      event.type === 'objective.pin_changed' &&
      event.objectiveId === objectiveId &&
      !reverted.has(event.eventId)
    ) {
      winning = { pinned: event.pinned, pinnedOrder: event.pinned ? event.pinnedOrder : null };
    }
  }

  if (winning) return winning;
  return { pinned: initialPinned ?? false, pinnedOrder: null };
}

interface ResolvedTargetDate {
  readonly targetDate: string;
  /** The winning adjustment event's ID, or null if none is active (domain-api.ts `appliedAdjustmentEventId`). */
  readonly appliedAdjustmentEventId: EventId | null;
}

/**
 * The effective target date for one objective, plus the eventId of the
 * adjustment that produced it: the `nextTargetDate` (and `eventId`) of the
 * most recent (canonical-order) `objective.target_date_adjusted` event for
 * this objective whose own `eventId` is NOT the target of a
 * `log_entry.reverted` event, or -- if every adjustment has been reverted,
 * or none were ever made -- the objective's original `objective.created`
 * `targetDate` with a null `appliedAdjustmentEventId`. Mirrors
 * `computeActionCompletionStatus`'s "revert makes the target as if absent"
 * rule (ADR-0002), adapted for a single mutable field (most-recent-
 * surviving-write) rather than a per-entity presence set.
 */
function resolveEffectiveTargetDate(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
): ResolvedTargetDate {
  const reverted = revertedEventIds(events);

  let originalTargetDate: string | null = null;
  let effectiveTargetDate: string | null = null;
  let effectiveAdjustmentEventId: EventId | null = null;

  for (const event of events) {
    if (event.type === 'objective.created' && event.objectiveId === objectiveId) {
      originalTargetDate = event.targetDate;
    } else if (
      event.type === 'objective.target_date_adjusted' &&
      event.objectiveId === objectiveId &&
      !reverted.has(event.eventId)
    ) {
      // Canonical (eventId) order means later matches in this forward walk
      // are more recent -- last surviving adjustment (and its eventId) wins.
      effectiveTargetDate = event.nextTargetDate;
      effectiveAdjustmentEventId = event.eventId;
    }
  }

  // Fall back to the original targetDate (and a null applied-adjustment
  // marker) if every adjustment was reverted (or none exist) -- never an
  // empty/undefined target date.
  return {
    targetDate: effectiveTargetDate ?? originalTargetDate ?? '',
    appliedAdjustmentEventId: effectiveTargetDate !== null ? effectiveAdjustmentEventId : null,
  };
}

function applyActionAdded(state: ObjectiveState, event: ActionAddedEvent): void {
  if (state.actionsById.has(event.actionId)) {
    // Defensive: an action.added for an ID that already exists (e.g. a
    // duplicate delivery that somehow was not deduped upstream). Treat as a
    // no-op on the entity map, but do not double-insert into order.
    return;
  }
  state.actionsById.set(event.actionId, {
    actionId: event.actionId,
    title: event.title,
    dueDate: event.dueDate,
    milestoneTag: event.milestoneTag,
    effortMinutes: event.effortMinutes ?? null,
    removed: false,
  });
  if (event.position === null || event.position >= state.order.length) {
    state.order.push(event.actionId);
  } else {
    const insertAt = Math.max(0, event.position);
    state.order.splice(insertAt, 0, event.actionId);
  }
}

function applyActionsReordered(
  state: ObjectiveState,
  event: ActionsReorderedEvent,
): void {
  const known = new Set(state.order);
  const requested = event.orderedActionIds.filter((id) => known.has(id));
  const requestedSet = new Set(requested);
  // Any known action not mentioned in the reorder payload keeps its relative
  // position, appended after the reordered ones -- defensive against a
  // partial reorder payload rather than silently dropping actions.
  const remainder = state.order.filter((id) => !requestedSet.has(id));
  state.order = [...requested, ...remainder];
}

/**
 * Completion status per actionId: an `action.completed` event exists for it
 * and has not itself been reverted (ADR-0002: revert makes the target "as if
 * absent"). Also returns the entryEventId (the completion event, if active)
 * and the date it completed on, needed by `ActionView`.
 */
export function computeActionCompletionStatus(
  events: readonly CompassEvent[],
): ReadonlyMap<
  ActionId,
  { readonly entryEventId: string; readonly completedOn: string }
> {
  const completions = eventsOfTypes(events, [
    'action.completed',
    'log_entry.reverted',
  ]);
  const active = excludingReverted(completions);

  const status = new Map<
    ActionId,
    { readonly entryEventId: string; readonly completedOn: string }
  >();
  for (const event of active) {
    if (event.type !== 'action.completed') continue;
    // Last (canonical-order) completion wins if there are duplicates for the
    // same actionId -- matches the "duplicates collapse" pattern used for
    // habit completions (ADR-0002).
    status.set(event.actionId, {
      entryEventId: event.eventId,
      completedOn: event.occurredOn,
    });
  }
  return status;
}

/**
 * The three-state DISPLAY status per actionId (R3.10, Phase 5, ADR-0002
 * addendum 2026-07-04 D3): `'done'` when `completion` (from
 * `computeActionCompletionStatus`) shows it completed -- completion ALWAYS
 * wins over any `action.status_changed` history, since `action.completed`'s
 * own presence is the sole source of truth for done-ness, unchanged by this
 * addition. Otherwise, the winning (last, non-reverted)
 * `action.status_changed` event's `status`, defaulting to `'to_do'` when
 * none exists (absent-event default, same convention as everywhere else in
 * this module).
 *
 * Deliberately does NOT consult `state.actionsById`/`removed` -- callers
 * already filter to non-removed actions before reading this map (mirrors
 * `computeActionCompletionStatus`'s own scope).
 */
export function computeActionDisplayStatus(
  events: readonly CompassEvent[],
  completion: ReadonlyMap<ActionId, { readonly entryEventId: string; readonly completedOn: string }>,
): ReadonlyMap<ActionId, ActionStatus | 'done'> {
  const reverted = revertedEventIds(events);
  const winning = new Map<ActionId, ActionStatus>();

  for (const event of events) {
    if (event.type !== 'action.status_changed') continue;
    if (reverted.has(event.eventId)) continue;
    // Canonical order -> later matches overwrite earlier ones (last wins).
    winning.set(event.actionId, event.status);
  }

  const result = new Map<ActionId, ActionStatus | 'done'>();
  const actionIds = new Set<ActionId>([...completion.keys(), ...winning.keys()]);
  for (const actionId of actionIds) {
    result.set(actionId, completion.has(actionId) ? 'done' : winning.get(actionId) ?? 'to_do');
  }
  return result;
}

/**
 * Resolve a single action's display status, defaulting to `'to_do'` when
 * neither completed nor ever status-changed -- a small convenience over
 * `computeActionDisplayStatus`'s map for call sites (like
 * `pickBestAction`'s next-up tie-break) that only need one action at a time
 * and would otherwise have to repeat the `?? 'to_do'` fallback themselves.
 */
export function resolveActionDisplayStatus(
  displayStatus: ReadonlyMap<ActionId, ActionStatus | 'done'>,
  actionId: ActionId,
): ActionStatus | 'done' {
  return displayStatus.get(actionId) ?? 'to_do';
}

/**
 * Symmetric objective-to-objective link resolution (R3.13, Phase 5, ADR-0002
 * addendum 2026-07-04 D4) -- the first many-to-many relationship in the
 * domain model. A link between A and B is "present" iff the count of
 * surviving `objective.linked` events for the unordered pair {A, B} is
 * strictly greater than the count of surviving `objective.unlinked` events
 * for the same pair (count-parity resolution, not last-write-by-timestamp --
 * see the addendum for why this avoids interleave-sorting two event types).
 * `objective.linked`/`objective.unlinked` are NOT revert targets (unlinking
 * is the natural undo of linking; a second undo mechanism would be
 * redundant), so `log_entry.reverted` is not consulted here.
 *
 * Returns every OTHER objectiveId currently linked to `objectiveId`, i.e. the
 * projection each side's `ObjectiveView.linkedObjectives` reads directly.
 */
export function resolveLinkedObjectiveIds(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
): ReadonlySet<ObjectiveId> {
  const parity = new Map<string, { readonly a: ObjectiveId; readonly b: ObjectiveId; count: number }>();

  for (const event of events) {
    if (event.type !== 'objective.linked' && event.type !== 'objective.unlinked') continue;
    const [a, b] =
      event.objectiveIdA < event.objectiveIdB
        ? [event.objectiveIdA, event.objectiveIdB]
        : [event.objectiveIdB, event.objectiveIdA];
    const key = `${a}:${b}`;
    const existing = parity.get(key) ?? { a, b, count: 0 };
    existing.count += event.type === 'objective.linked' ? 1 : -1;
    parity.set(key, existing);
  }

  const linked = new Set<ObjectiveId>();
  for (const { a, b, count } of parity.values()) {
    if (count <= 0) continue;
    if (a === objectiveId) linked.add(b);
    else if (b === objectiveId) linked.add(a);
  }
  return linked;
}
